/**
 * API version constants and helpers.
 * Use version param in API.common({ url, data, version: 'v1' | 'v2', ... })
 * to target /api/v1 or /api/v2. If version is omitted, DEFAULT_API_VERSION is used
 * when the URL does not already contain a version segment.
 */

export const API_VERSIONS = Object.freeze({
	V1: 'v1',
	V2: 'v2',
});

export const DEFAULT_API_VERSION = API_VERSIONS.V1;

/** @type {ReadonlyArray<string>} */
export const SUPPORTED_VERSIONS = Object.freeze([API_VERSIONS.V1, API_VERSIONS.V2]);

/**
 * Builds a path with the given API version.
 * - If url already contains /v1/ or /v2/, that segment is replaced with the given version.
 * - Otherwise the version is injected: /api/... -> /api/{version}/..., /... -> /{version}/...
 *
 * @param {string} url - Request path (e.g. '/search', '/api/v2/create')
 * @param {string} [version] - 'v1' | 'v2'; if omitted, url is returned unchanged
 * @returns {string} Path with version applied
 */
export function buildVersionedUrl(url, version) {
	if (!url || typeof url !== 'string') return url;
	if (!version || !SUPPORTED_VERSIONS.includes(version)) return url;

	const normalizedVersion = version.toLowerCase();

	// Already versioned: replace existing /v1/ or /v2/ with requested version
	if (/\/v[12]\//.test(url)) {
		return url.replace(/\/v[12]\//, `/${normalizedVersion}/`);
	}
	if (/^\/v[12]\//.test(url) || /^\/v[12]$/.test(url)) {
		return url.replace(/^\/v[12](\/|$)/, `/${normalizedVersion}$1`);
	}

	// Not versioned: inject version
	// /api/create -> /api/v2/create; /search -> /v2/search
	if (url.startsWith('/api/')) {
		return url.replace(/^(\/api)\//, `$1/${normalizedVersion}/`);
	}
	// Paths like /search, /update, /create
	if (url.startsWith('/')) {
		return `/${normalizedVersion}${url}`;
	}
	return `/${normalizedVersion}/${url}`;
}
